import assert from 'assert';

import {
  SaveToResultsStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrame} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {OUTPUT_NAME_SEPARATOR} from 'analysis-tool/outputs/Types';
import {InputTableApplyState, InputTableType} from 'analysis-tool/templates/AnalysisTemplateUtil';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getParentSteps, getTopLevelTableStartStepForViewLevelStep} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';

export function isSendToEntryEnabled(): boolean {
  return BenchlingConfig.ENABLE_ANALYSIS_TOOL_CHARTS_V2 && BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS;
}
export function getOutputDataFrameName(
  template: PipelineGraphQL,
  templateStep: GetAnalysisPipelineStep,
  inputTables: Array<InputTableApplyState>
): string {
  const parents = getParentSteps(template, templateStep);
  for (const parent of parents) {
    const inputTable = inputTables.find((inputTable) => inputTable.templateLineageNode.step.id === parent.id);
    if (inputTable) {
      if (inputTable.lineageNode == null) {
        return 'Input table';
      }
      // If the output step's direct parent is an input step, use the input table's dataframe name
      return inputTable.lineageNode.data.dataFrame.name;
    }
  }
  const stepDataName =
    templateStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS
      ? SaveToResultsStepDataName.INPUT_DATASET
      : SINGLE_DATASET_INPUT_STEP_DATA_NAME;
  return findAndAssertSingleStepDataFrame(templateStep, stepDataName, true).name;
}

export function getOutputNameFromStep(
  template: PipelineGraphQL,
  analysisName: string,
  templateStep: GetAnalysisPipelineStep,
  inputTables: Array<InputTableApplyState>
): string {
  const outputName = getOutputDataFrameName(template, templateStep, inputTables);
  let maybeTopLevelStartStep = getTopLevelTableStartStepForViewLevelStep(template, templateStep);
  if (maybeTopLevelStartStep != null) {
    const inputTable = inputTables.find(
      (inputTable) =>
        inputTable.inputType === InputTableType.VARIABLE &&
        inputTable.templateLineageNode.step.id === maybeTopLevelStartStep!.id
    );
    maybeTopLevelStartStep = inputTable ? inputTable.lineageNode?.step! : maybeTopLevelStartStep;
  }
  const nameParts = [analysisName]
    .concat(maybeTopLevelStartStep ? [maybeTopLevelStartStep.outputStepDatas[0]?.dataFrames[0].name] : [])
    .concat([outputName]);
  return nameParts.join(OUTPUT_NAME_SEPARATOR);
}

export function getOutputNameFromOutput(
  analysisName: string,
  output: {outputName: string; parentOutputName: string | null}
): string {
  return `${[analysisName]
    .concat(output.parentOutputName ? [output.parentOutputName] : [])
    .concat(output.outputName)
    .join(OUTPUT_NAME_SEPARATOR)}`;
}

export function getDocumentIdFromStep(step: GetAnalysisPipelineStep): string {
  assert(
    step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT,
    `Expected SEND_TO_DOCUMENT_STEP, found ${step.definition.stepType}`
  );
  return getPipelineStepConfig(step, PipelineStepType.SEND_TO_DOCUMENT, 0).documentId;
}
